import { Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import type { Editor } from 'src/pages/datastores/document-detail/paragraph/editor/index.ts'
import ImageUploadFail from '@/assets/dataset/paragraph/image-upload-fail.svg?url'
import {
  RenderReactComponent,
  insertElementToSelection,
  isSelectionInTargetNode,
} from './dom.tsx'

export function withPaste(
  instance: Editor,
  onImageUpload?: (file: File) => Promise<string>,
) {
  const editor = instance.editor
  if (editor) {
    editor.addEventListener('paste', e => {
      e.preventDefault()
      if (onImageUpload) {
        dealWithImagePaste(e, instance, onImageUpload)
      }
      const textValue = e.clipboardData?.getData('text/plain') ?? ''
      if (textValue.length) {
        document.execCommand('insertHtml', false, textValue)
      }
    })
  }
}

async function dealWithImagePaste(
  e: globalThis.ClipboardEvent,
  instance: Editor,
  onImageUpload: (file: File) => Promise<string>,
) {
  const items = e.clipboardData?.items ?? []
  const editor = instance.editor!
  const imageFiles: File[] = []

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.kind === 'file') {
      const file = item.getAsFile()
      const imageTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/bmp',
        'image/svg+xml',
      ]
      file && imageTypes.includes(file.type) && imageFiles.push(file)
    }
  }

  if (imageFiles.length) {
    await insertImage(editor, imageFiles, onImageUpload)
    instance.listener.onChange?.()
  }
}

export function insertImage(
  editor: HTMLDivElement,
  files: File[],
  onImageUpload: (file: File) => Promise<string>,
  selectionInfo?: any,
) {
  return new Promise(resolve => {
    files.forEach(file => {
      const LoadingBox = document.createElement('div')
      LoadingBox.className = 'paragraph-editor-image-upload-loading-box'

      RenderReactComponent(Spin, LoadingBox, {
        size: 'small',
        indicator: (
          <LoadingOutlined
            className='!flex c-primary'
            style={{ fontSize: 24 }}
          />
        ),
      })
      if (selectionInfo) {
        const selection = window.getSelection()!
        try {
          selection.removeAllRanges() // 清除当前的选择
        } catch (ex) {
          // 兼容IE
          ;(document.body as any)
            ?.createTextRange?.()
            ?.select?.()(
              // IE的特殊处理
              (document as any)?.selection,
            )
            ?.empty?.()
        }
        selection.addRange(selectionInfo) // 添加保存的选择范围
      }
      const isInTargetContainer = isSelectionInTargetNode(editor)
      if (isInTargetContainer) {
        insertElementToSelection(LoadingBox, editor)
      } else {
        editor.appendChild(LoadingBox)
      }

      const img = document.createElement('img')
      img.alt = file.name

      onImageUpload(file)
        .then(res => {
          img.src = res
        })
        .catch(() => {
          img.src = ImageUploadFail
        })
        .finally(() => {
          LoadingBox.parentNode?.replaceChild(img, LoadingBox)
          resolve?.('')
        })
    })
  })
}
